import type { CreateDeliveryProposalFormSchemaType } from "@/pages/carrier-delivery-proposal-create/model/validation";
import { formatMoney } from "@/shared/lib/currency";
import { fromIso8601Date, toLocaleTextAsDate } from "@/shared/lib/datetime";
import { BaseNumberField } from "@/shared/ui/base-number-field";
import { MobileDeliveryProposalFormTable } from "@/widgets/delivery-proposal-form-table/mobile-delivery-proposal-form-table";
import { Box } from "@mui/material";
import { type Control, Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DeliveryProposalFormLayout } from "../../../../../widgets/delivery-proposal-form-layout";
import { DeliveryProposalFormTable } from "../../../../../widgets/delivery-proposal-form-table";
import type { CarrierDeliveryProposalCreatePageProps } from "../../../model";

export const PaymentForm = (props: {
  control: Control<CreateDeliveryProposalFormSchemaType>;
  draftOfferCargo: CarrierDeliveryProposalCreatePageProps["draftOfferCargo"];
}) => {
  const { t } = useTranslation();

  const { control } = props;

  // shippingFeeが0の場合「提案してください」と表示
  const formatShippingFeeForProposal = (money: number | null | undefined) => {
    if (money === null || money === undefined) {
      return null;
    }
    if (money === 0) {
      return "提案してください";
    }
    return formatMoney(money);
  };

  const shippingFeeCells = [
    formatShippingFeeForProposal(props.draftOfferCargo.data?.shippingFee),
    <Controller
      key="proposal"
      name="shippingFee"
      control={control}
      render={({ field, fieldState }) => {
        return (
          <BaseNumberField
            {...field}
            size="small"
            fullWidth
            startAdornment="¥"
            error={!!fieldState.invalid}
            helperText={fieldState.error?.message}
          />
        );
      }}
    />,
  ];

  const highwayFeeCells = [
    props.draftOfferCargo.data?.highwayFee &&
    props.draftOfferCargo.data.highwayFee > 0
      ? "あり"
      : "なし",
    null,
  ];

  const highwaySectionCells = [
    props.draftOfferCargo.data?.highwaySection,
    null,
  ];

  const fuelSurchargeCells = [
    formatMoney(props.draftOfferCargo.data?.fuelSurcharge),
    null,
  ];

  const cargoHandlingFeeCells = [
    formatMoney(props.draftOfferCargo.data?.cargoHandlingFee),
    null,
  ];

  const othersFeeCells = [
    formatMoney(props.draftOfferCargo.data?.othersFee),
    null,
  ];

  const adjustmentFeeCells = [
    formatMoney(props.draftOfferCargo.data?.adjustmentFee),
    null,
  ];

  const paymentDateCells = [
    props.draftOfferCargo.data?.paymentDate
      ? toLocaleTextAsDate(
          fromIso8601Date(props.draftOfferCargo.data?.paymentDate),
        )
      : null,
    null,
  ];

  const basicFeeMemoCells = [props.draftOfferCargo.data?.basicFeeMemo, null];

  return (
    <DeliveryProposalFormLayout title={"運賃"} isLoading={false}>
      <div>
        {/* PC用のフォーム */}
        <Box display={{ xs: "none", sm: "block" }}>
          <DeliveryProposalFormTable
            headers={["荷物", "提案"]}
            rows={[
              {
                title: t("offerCargo.shippingFee"),
                cells: shippingFeeCells,
              },
              {
                title: t("offerCargo.highwayFee"),
                cells: highwayFeeCells,
              },
              {
                title: t("offerCargo.highwaySection"),
                cells: highwaySectionCells,
              },
              {
                title: t("offerCargo.fuelSurcharge"),
                cells: fuelSurchargeCells,
              },
              {
                title: t("offerCargo.cargoHandlingFee"),
                cells: cargoHandlingFeeCells,
              },
              {
                title: t("offerCargo.othersFee"),
                cells: othersFeeCells,
              },
              {
                title: t("offerCargo.adjustmentFee"),
                cells: adjustmentFeeCells,
              },
              {
                title: t("offerCargo.paymentDate"),
                cells: paymentDateCells,
              },
              {
                title: t("offerCargo.basicFeeMemo"),
                cells: basicFeeMemoCells,
              },
            ]}
          />
        </Box>

        {/* モバイル用のフォーム */}
        <Box display={{ xs: "block", sm: "none" }}>
          <MobileDeliveryProposalFormTable
            items={[
              {
                label: t("offerCargo.shippingFee"),
                cargo: shippingFeeCells[0],
                proposal: shippingFeeCells[1],
              },
              {
                label: t("offerCargo.highwayFee"),
                cargo: highwayFeeCells[0],
                proposal: highwayFeeCells[1],
              },
              {
                label: t("offerCargo.highwaySection"),
                cargo: highwaySectionCells[0],
                proposal: highwaySectionCells[1],
              },
              {
                label: t("offerCargo.fuelSurcharge"),
                cargo: fuelSurchargeCells[0],
                proposal: fuelSurchargeCells[1],
              },
              {
                label: t("offerCargo.cargoHandlingFee"),
                cargo: cargoHandlingFeeCells[0],
                proposal: cargoHandlingFeeCells[1],
              },
              {
                label: t("offerCargo.othersFee"),
                cargo: othersFeeCells[0],
                proposal: othersFeeCells[1],
              },
              {
                label: t("offerCargo.adjustmentFee"),
                cargo: adjustmentFeeCells[0],
                proposal: adjustmentFeeCells[1],
              },
              {
                label: t("offerCargo.paymentDate"),
                cargo: paymentDateCells[0],
                proposal: paymentDateCells[1],
              },
              {
                label: t("offerCargo.basicFeeMemo"),
                cargo: basicFeeMemoCells[0],
                proposal: basicFeeMemoCells[1],
              },
            ]}
          />
        </Box>
      </div>
    </DeliveryProposalFormLayout>
  );
};
